import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { subdivisionsApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { DollarSign, Tag } from 'lucide-react';

const SALE_STATUS_OPTIONS = [
  { value: 'None', label: 'Available', color: 'bg-gray-100 text-gray-700' },
  { value: 'Listed', label: 'Listed', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'Under_Contract', label: 'Under Contract (Sell)', color: 'bg-amber-100 text-amber-700' },
  { value: 'Sold_Cash', label: 'Sold (Cash)', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'Sold_Note', label: 'Sold (Note Held)', color: 'bg-blue-100 text-blue-700' },
];

export const ChildLotManagerDialog = ({ 
  open, 
  onOpenChange, 
  childLot, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [saleStatus, setSaleStatus] = useState('None');
  const [salePrice, setSalePrice] = useState('');
  const [saleDate, setSaleDate] = useState('');

  // Reset form when childLot changes
  useEffect(() => {
    if (childLot) {
      setSaleStatus(childLot.subdivision_sale_status || 'None');
      setSalePrice(childLot.sale_price?.toString() || '');
      setSaleDate(childLot.sale_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
    } else {
      setSaleStatus('None');
      setSalePrice('');
      setSaleDate(new Date().toISOString().split('T')[0]);
    }
  }, [childLot, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!childLot) return;

    setLoading(true);
    try {
      // Update sale status
      await subdivisionsApi.updateChildSaleStatus(childLot.id, {
        sale_status: saleStatus,
        sale_price: salePrice ? parseFloat(salePrice) : null,
        sale_date: (saleStatus.startsWith('Sold') && saleDate) ? saleDate : null
      });

      onSuccess();
    } catch (error) {
      console.error('Failed to update child lot:', error);
      toast.error(error.response?.data?.detail || 'Failed to update child lot');
    } finally {
      setLoading(false);
    }
  };

  const isSoldStatus = saleStatus?.startsWith('Sold');

  if (!childLot) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Tag className="w-5 h-5 text-purple-600" />
            Manage Child Lot
          </DialogTitle>
          <DialogDescription>
            Update the sale status and pricing for this subdivided lot.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Lot Info Summary */}
          <div className="p-3 bg-muted/30 rounded-sm">
            <div className="flex items-center justify-between">
              <p className="font-medium">{childLot.name}</p>
              <Badge variant="outline" className="text-xs rounded-sm">
                {childLot.cost_basis_percentage}% allocation
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Cost Basis: {formatCurrency(childLot.purchase_price)}
            </p>
          </div>

          {/* Sale Status */}
          <div className="space-y-2">
            <Label htmlFor="sale_status">Sale Status</Label>
            <Select value={saleStatus} onValueChange={setSaleStatus}>
              <SelectTrigger className="rounded-sm" data-testid="child-sale-status-select">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="rounded-sm">
                {SALE_STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${opt.color.split(' ')[0]}`} />
                      {opt.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {saleStatus === 'None' && 'Lot is available and not yet marketed'}
              {saleStatus === 'Listed' && 'Lot is actively being marketed for sale'}
              {saleStatus === 'Under_Contract' && 'Lot has an accepted offer and is pending closing'}
              {saleStatus === 'Sold_Cash' && 'Lot has been sold for cash'}
              {saleStatus === 'Sold_Note' && 'Lot has been sold with owner financing (note held)'}
            </p>
          </div>

          {/* Sale Price - shown for all statuses so you can set expected price */}
          <div className="space-y-2">
            <Label htmlFor="sale_price" className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              {isSoldStatus ? 'Sale Price' : 'Expected/Listed Price'}
            </Label>
            <Input
              id="sale_price"
              type="number"
              step="0.01"
              min="0"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              placeholder="Enter price"
              className="rounded-sm font-mono"
              data-testid="child-sale-price-input"
            />
          </div>

          {/* Sale Date - only shown for sold statuses */}
          {isSoldStatus && (
            <div className="space-y-2">
              <Label htmlFor="sale_date">Sale Date</Label>
              <Input
                id="sale_date"
                type="date"
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                className="rounded-sm"
                data-testid="child-sale-date-input"
              />
            </div>
          )}

          {/* Profit Preview when sold */}
          {isSoldStatus && salePrice && (
            <div className="p-3 bg-emerald-50 rounded-sm border border-emerald-200">
              <p className="text-xs uppercase tracking-wide text-emerald-700 font-medium">Profit Preview</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm text-emerald-600">Sale - Cost Basis</span>
                <span className="font-mono font-bold text-emerald-700">
                  {formatCurrency((parseFloat(salePrice) || 0) - (childLot.purchase_price || 0))}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
              data-testid="save-child-lot-btn"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChildLotManagerDialog;
